import React, { Fragment, useEffect, useRef, useState } from 'react';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import COLORS from '@zola/zola-ui/src/styles/emotion/colors';

import cx from 'classnames';

import { OverflowEllipsis, OverflowEllipsisProps } from '../OverflowEllipsis/OverflowEllipsis';

import styles from './viewMore.module.less';

export type ViewMoreProps = OverflowEllipsisProps & {
  backgroundColor?: string;
};

/**
 * Provides up to N lines of text, and if there is more text, includes a View More link
 * to expand the selection to as large as it needs to be
 */
export const ViewMore: React.FC<ViewMoreProps> = (props) => {
  const { backgroundColor = COLORS.WHITE, className, children, ...rest } = props;

  const viewMoreRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fadeStyle = {
    backgroundImage: `linear-gradient(to right, transparent, ${backgroundColor} 80%)`,
  };

  // Make sure the component is visible before reading scroll & client heights
  useEffect(() => {
    const { current } = viewMoreRef;

    if (current) {
      const observer = new IntersectionObserver((entries) => {
        const intersectingEntry = entries.find((entry) => entry.isIntersecting);

        if (intersectingEntry) {
          const { scrollHeight, clientHeight } = intersectingEntry.target;
          setShowControls(scrollHeight !== clientHeight);
        }

        observer.unobserve(current);
      });

      observer.observe(current);
    }
  }, [viewMoreRef]);

  return (
    <OverflowEllipsis
      className={cx(className, styles.viewMore, { [styles.open]: isOpen })}
      ref={viewMoreRef}
      style={{ backgroundColor }}
      {...rest}
    >
      {children}
      {showControls && (
        <Fragment>
          <div className={styles.more}>
            <div className={styles.fade} style={fadeStyle} />
            <LinkV2
              className={styles.button}
              role="button"
              type="button"
              onClick={() => setIsOpen(true)}
            >
              View more
            </LinkV2>
          </div>
          <div className={styles.less}>
            &ensp;
            <LinkV2
              className={styles.button}
              role="button"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              View less
            </LinkV2>
          </div>
        </Fragment>
      )}
    </OverflowEllipsis>
  );
};
